import { useState, useCallback, useRef } from "react";
import { Search, SearchX, X, Plus } from "lucide-react";
import { IPlayerDetail } from "../../models/IPlayer";
import { ITeamPlayer } from "../../models/IMatch";
import { searchPlayers } from "../../api/players";
import { registerIndividual } from "../../api/tournaments";
import { Avatar, Button, Dialog, EmptyState, IconButton, Input, Loader, SearchInput, useDialogs } from "../../ui";
import "./styles.scss";

type AddIndividualPlayerDialogProps = {
  tournamentCategoryId: string;
  currentPlayers: IPlayerDetail[];
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const AddIndividualPlayerDialog = (props: AddIndividualPlayerDialogProps) => {
  const { alert } = useDialogs();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [players, setPlayers] = useState<ITeamPlayer[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<ITeamPlayer | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showNewPlayer, setShowNewPlayer] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchPlayers = useCallback(
    async (search: string) => {
      if (!search.trim()) {
        setPlayers([]);
        return;
      }

      setLoading(true);
      try {
        const { data } = await searchPlayers(search);
        // Map the API response to add playerId field (API returns "id" but we need "playerId")
        const mappedPlayers = data.map((p: any) => ({
          ...p,
          playerId: p.id, // Map id to playerId
        }));
        // Filter out players already registered in the category
        const currentPlayerIds = props.currentPlayers.map((p) => p.playerId);
        const availablePlayers = mappedPlayers.filter((p) => !currentPlayerIds.includes(p.playerId));
        setPlayers(availablePlayers);
      } catch (error) {
        console.error("Error fetching players:", error);
        setPlayers([]);
      } finally {
        setLoading(false);
      }
    },
    [props.currentPlayers]
  );

  const debouncedSearch = useCallback(
    (value: string) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        fetchPlayers(value);
      }, 500);
    },
    [fetchPlayers]
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Clear old results when search term changes
    if (value.trim() !== searchTerm.trim()) {
      setPlayers([]);
    }
    debouncedSearch(value);
  };

  // The kit Dialog plays the exit animation itself: reset the picker and hand the close to the parent right away.
  const handleClose = () => {
    setSelectedPlayer(null);
    setSearchTerm("");
    setPlayers([]);
    setShowNewPlayer(false);
    setNewFirstName("");
    setNewLastName("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    props.onClose();
  };

  const handleSelectPlayer = (player: ITeamPlayer) => {
    setSelectedPlayer(player);
  };

  const handleRemovePlayer = () => {
    setSelectedPlayer(null);
  };

  // Add a non-registered ("guest") player: no account, name only. Fills the same
  // single-selection slot as a searched player (mutually exclusive).
  const handleAddUnregisteredPlayer = () => {
    const name = `${newFirstName.trim()} ${newLastName.trim()}`.trim();
    if (!name) return;
    const fakeId = `unregistered_${Date.now()}`;
    setSelectedPlayer({
      id: fakeId,
      playerId: fakeId,
      firstName: newFirstName.trim(),
      lastName: newLastName.trim(),
      nationality: "",
      _isUnregistered: true,
      _playerName: name,
    } as any);
    setNewFirstName("");
    setNewLastName("");
    setShowNewPlayer(false);
  };

  // Filter out selected player from search results
  const visiblePlayers = players.filter(
    (player) => !selectedPlayer || selectedPlayer.playerId !== player.playerId
  );

  const handleConfirm = async () => {
    if (!selectedPlayer) {
      handleClose();
      return;
    }

    setSubmitting(true);
    try {
      const isGuest = (selectedPlayer as any)._isUnregistered;
      await registerIndividual(
        props.tournamentCategoryId,
        isGuest
          ? { playerName: (selectedPlayer as any)._playerName }
          : { playerId: selectedPlayer.id }
      );

      props.onConfirm();
      handleClose();
    } catch (error: any) {
      console.error("Error registering player:", error);
      await alert(error.message || "Failed to register player");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      title="Add Player"
      size="md"
      busy={submitting}
      bodyClassName="add-individual-player"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} loading={submitting} disabled={!selectedPlayer}>
            {submitting ? "Adding..." : "Add Player"}
          </Button>
        </>
      }
    >
      <div className="add-individual-player__search">
        <SearchInput
          block
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={handleSearch}
          autoFocus
        />
      </div>

      <div className="add-individual-player__content">
        {selectedPlayer && (
          <section className="add-individual-player__selected">
            <p className="add-individual-player__section-title add-individual-player__section-title--spark">Selected Player</p>
            <div className="add-individual-player__row add-individual-player__row--selected">
              <Avatar name={`${selectedPlayer.firstName} ${selectedPlayer.lastName || ""}`} size={32} />
              <span className="add-individual-player__info">
                <span className="add-individual-player__name">
                  {selectedPlayer.firstName} {selectedPlayer.lastName || ""}
                </span>
                <span className="add-individual-player__details">
                  {(selectedPlayer as any)._isUnregistered
                    ? "Non-registered player"
                    : selectedPlayer.nationality}
                </span>
              </span>
              <IconButton
                label="Remove player"
                icon={<X />}
                variant="danger"
                size="sm"
                onClick={handleRemovePlayer}
              />
            </div>
          </section>
        )}

        {!searchTerm.trim() ? (
          <EmptyState compact icon={<Search />} title="Type to search for players by full name, nickname or email" />
        ) : loading ? (
          <Loader label="Searching..." minHeight={140} />
        ) : visiblePlayers.length === 0 ? (
          <EmptyState compact icon={<SearchX />} title="No players found matching your search" />
        ) : (
          <section>
            <p className="add-individual-player__section-title">Search Results</p>
            <ul className="add-individual-player__list">
              {visiblePlayers.map((player) => (
                <li key={player.playerId}>
                  <button
                    type="button"
                    className="add-individual-player__row add-individual-player__pick"
                    onClick={() => handleSelectPlayer(player)}
                  >
                    <Avatar name={`${player.firstName} ${player.lastName || ""}`} size={32} />
                    <span className="add-individual-player__info">
                      <span className="add-individual-player__name">
                        {player.firstName} {player.lastName || ""}
                      </span>
                      <span className="add-individual-player__details">{player.nationality}</span>
                    </span>
                    <Plus className="add-individual-player__pick-icon" size={16} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Add non-registered (guest) player — name only, no account */}
        {!showNewPlayer ? (
          <Button variant="link" icon={<Plus />} onClick={() => setShowNewPlayer(true)}>
            Add non-registered player
          </Button>
        ) : (
          <div className="add-individual-player__new-player">
            <Input
              inputSize="sm"
              placeholder="First name *"
              value={newFirstName}
              onChange={(e) => setNewFirstName(e.target.value)}
              autoFocus
            />
            <Input
              inputSize="sm"
              placeholder="Last name"
              value={newLastName}
              onChange={(e) => setNewLastName(e.target.value)}
            />
            <Button
              variant="primary"
              size="sm"
              disabled={!newFirstName.trim()}
              onClick={handleAddUnregisteredPlayer}
            >
              Add
            </Button>
            <IconButton
              label="Cancel"
              icon={<X />}
              size="sm"
              onClick={() => {
                setShowNewPlayer(false);
                setNewFirstName("");
                setNewLastName("");
              }}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default AddIndividualPlayerDialog;
